import React, { useState, useEffect } from 'react';
import { useRedirect, Show, SimpleShowLayout, ImageField, useTranslate, Labeled, useDataProvider } from 'react-admin';
import { Typography, Box, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import CustomUrlField from '../common_modules/components/CustomUrlField';
import { getLocalStorageId } from '../authProvider';
import { useOAuthCallback } from '../common_modules/hooks/useOAuthCallback';

const NoActions = () => null;

export const InviteUrlShow = () => {
  const translate = useTranslate();
  const recordId = getLocalStorageId();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const { isLoading: isOAuthLoading } = useOAuthCallback();
  const [isSecondaryAllowed, setIsSecondaryAllowed] = useState(false);
  const [dynamicData, setDynamicData] = useState<{ inviteCodeUrl: string; qrCode: string } | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!recordId) {
      redirect('/login');
    }
  }, [recordId, redirect]);

  useEffect(() => {
    if (!recordId) return;

    const fetchDynamicUrl = async () => {
      setIsFetching(true);
      try {
        const isCompany = !isSecondaryAllowed; // ON -> false, OFF -> true
        const response = await dataProvider.getData(`settings/${recordId}/dynamic-url?isCompany=${isCompany}`);
        if (response) {
          setDynamicData(response);
        }
      } catch (error) {
        console.error('Failed to fetch dynamic url', error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchDynamicUrl();
  }, [recordId, isSecondaryAllowed, dataProvider]);

  // ★ isOAuthLoadingがtrueの間（連携処理中）は、ローディング画面を表示する
  if (isOAuthLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="50vh" mt="1rem">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>STORES.jpとの連携を処理中です。しばらくお待ちください...</Typography>
      </Box>
    );
  }

  if (!recordId) {
    return null;
  }

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSecondaryAllowed(event.target.checked);
  };

  return (
    <Show
      resource="settings"
      id={recordId} // ここで recordId は string 型として渡されることが保証される
      title={translate('resources.settings.fields.inviteCodeUrl')}
      actions={<NoActions />}
      sx={{
        mt: '1rem'
      }}
    >
      <SimpleShowLayout
        sx={{
          mt: 1,
          p: '16px'
        }}
        spacing={2}
        record={dynamicData || undefined}
      >
        <Typography
          sx={{
            fontSize: '14px'
          }}
        >
          以下のURL、QRコードを使用して新規パートナーを招待してください。
        </Typography>

        {isFetching || !dynamicData ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Labeled label={translate('resources.settings.fields.inviteCodeUrl')} sx={{ display: 'block' }}>
              <CustomUrlField source="inviteCodeUrl" />
            </Labeled>
            <Labeled label="QRコード" sx={{ mt: 2, display: 'block' }}>
              <ImageField source="qrCode" label="QRコード" sx={{ mt: 2 }} />
            </Labeled>
          </>
        )}

        <Box display="flex" alignItems="center" gap={1} {...({ label: false } as any)}>
          <Switch
            checked={isSecondaryAllowed}
            onChange={handleToggleChange}
            color="primary"
          />
          <Typography>
            2次パートナー招待を許可する
          </Typography>
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};
